import React, { useEffect, useState } from 'react';
import Table from '../../components/common/table/Table';
import { FaFileExcel, FaTrash } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import ActionButtons from '../../components/common/ActionButtons';
import { useBts } from '../../context/btsContext/BtsContextProvider';
import { useNavigate } from 'react-router-dom'; // <-- Add this import
import { useDebouncedCallback } from '@/utils/useDebouncedCallback';
import {
  TbCircleNumber1,
  TbCircleNumber2,
  TbCircleNumber3,
  TbCircleNumber4,
} from 'react-icons/tb';
import Step2Report from '@/pages/bts/Step2Report';
import { getClassListForProposal } from '@/services/BtsService';
import { showAlert } from '@/components';
import {
  ModalProvider,
  useModal,
} from '@/components/common/modal/ModalContext';

const BtsTable = () => {
  const {
    getAllBtsProposals,
    btsTableData,
    totalElements,
    currentPage,
    setCurrentPage,
    pageSize,
    setSortBy,
    setSortDir,
    sortBy,
    sortDir,
    searchQuery,
    setSearchQuery,
    setBtsSchool,
    deleteSchoolProposal, // <-- use from context
    exportBTSExcelReport,
    exportBtsShReportAPI,
    kitImportTemplateReportAPI,
    exportContractExportReportAPI,
  } = useBts();
  const { openModal, closeModal } = useModal();

  const navigate = useNavigate(); // <-- Add this line
  const [inputValue, setInputValue] = useState('');

  const handleStep2Report = async (proposalId, schoolName) => {
    try {
      //APi Call
      const response = await getClassListForProposal(proposalId);
      // If API Res [] -> retrun
      if (!response || response.length === 0) {
        showAlert('error', 'Products for this class or year does not exist');
        return;
      }
      openModal(
        <Step2Report
          proposalId={proposalId}
          data={response}
          schoolName={schoolName}
          onClose={closeModal}
        />,
      );
    } catch (error) {
      console.error('Error in fetching step 2 report data');
    }
  };

  useEffect(() => {
    getAllBtsProposals();
    // eslint-disable-next-line
  }, [currentPage, searchQuery, sortBy, sortDir]);

  const handleSortChange = ({ field, direction }) => {
    setSortBy(field);
    setSortDir(direction);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
    getAllBtsProposals(0, pageSize, e.target.value, sortBy, sortDir);
  };

  const debouncedSetSearchQuery = useDebouncedCallback((val) => {
    setSearchQuery(val);
    setCurrentPage(0);
  }, 300);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    debouncedSetSearchQuery.call(val);
  };

  // Edit handler: set BTS school data and navigate
  const handleEdit = (row) => {
    setBtsSchool((prev) => ({
      ...prev,
      proposal: { ...row },
    }));
    navigate('/bts/create-proposal');
  };

  const handleDelete = async (row) => {
    try {
      await deleteSchoolProposal(row.id);
      // getAllBtsProposals(); // Not needed, context handler already refreshes
    } catch (err) {
      // Optionally handle error (toast, etc.)
    }
  };

  const columns = [
    { header: 'Proposal Id', field: 'id' },
    {
      header: 'Created Date',
      field: 'createdDate',
      render: (row) => {
        if (!row.createdDate) return '';
        const date = new Date(row.createdDate);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      },
    },
    { header: 'School Name', field: 'schoolName' },
    { header: 'Email', field: 'email' },
    { header: 'Contact', field: 'phoneNumber' },
    { header: 'Business Manager', field: 'businessManager' },
    { header: 'Booklist Type', field: 'booklistType' },
    {
      header: 'Sales Rep',
      field: 'salesRep.firstName',
      render: (row) => row.salesRep?.firstName || '',
    },
    {
      header: 'ACTIONS',
      render: (row) => (
        <ActionButtons
          actions={[
            {
              icon: <FaFileExcel />,
              onClick: () => exportBTSExcelReport(row.id),
              tooltip: 'Export to Excel',
              className: 'text-green-500 pr-5',
            },
            {
              icon: <MdEdit />,
              onClick: () => handleEdit(row),
              tooltip: 'Edit Proposal',
              className: 'text-gray-500 pr-5',
            },
            {
              icon: <FaTrash />,
              onClick: () => handleDelete(row),
              tooltip: 'Delete Proposal',
              className: 'text-red-500 pr-5',
            },
          ]}
        />
      ),
    },
    {
      header: 'Step Report',
      render: (row) => (
        <ActionButtons
          actions={[
            {
              icon: <TbCircleNumber1 />,
              onClick: () => exportBtsShReportAPI(row.id, row.schoolName),
              tooltip: 'Step 1 Report',
              className: 'text-black-500 pr-5 text-2xl',
            },
            {
              icon: <TbCircleNumber2 />,
              onClick: () => handleStep2Report(row.id, row.schoolName),
              tooltip: 'Step 2 Report',
              className: 'text-black-500 pr-5 text-2xl',
            },
            {
              icon: <TbCircleNumber3 />,
              onClick: () => kitImportTemplateReportAPI(row.id, row.schoolName),
              tooltip: 'Step 3 Report(Kit Import)',
              className: 'text-black-500 pr-5 text-2xl',
            },
            {
              icon: <TbCircleNumber4 />,
              onClick: () =>
                exportContractExportReportAPI(row.id, row.schoolName),
              tooltip: 'Step 4 Report(Contract Export)',
              className: 'text-black-500 pr-5 text-2xl',
            },
          ]}></ActionButtons>
      ),
    },
  ];

  return (
    <div>
      <div className="mr-4 mb-4 flex justify-end">
        <input
          type="text"
          placeholder="Search School..."
          value={inputValue}
          onChange={handleSearchChange}
          className="border px-2 py-1 rounded"
        />
      </div>
      <Table
        columns={columns}
        data={btsTableData}
        totalElements={totalElements}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={(newPage) => setCurrentPage(newPage)}
        onSortChange={handleSortChange}
        sortConfig={{ field: sortBy, direction: sortDir }}
      />
    </div>
  );
};

const App = () => (
  <ModalProvider>
    <BtsTable />
  </ModalProvider>
);

export default App;
